/**
 * The phone's Back button closes the popup on top, not the page under it.
 *
 * Android's system Back (and a mobile browser's) walks the webview's history.
 * Tauri's Android shell does exactly that by default — `goBack()` while the
 * webview can, otherwise the app closes — so with a menu, a dialog or the
 * full-screen peek open, Back used to leave the route behind it, or quit the
 * app from the first screen. Every phone app closes the sheet instead.
 *
 * The mechanism is one *guard* history entry, not one per popup:
 *
 * - While anything is open, the current history entry is a guard: a copy of
 *   the entry below it (same URL, same router state) with {@link GUARD_KEY}
 *   added. Back pops it, the URL does not change, and the router sees nothing
 *   new. That `popstate` closes the **topmost** popup only.
 * - If something is still open afterwards (a second popup, or one that
 *   refused — the peek asking about unsaved edits), the guard is pushed again,
 *   so the next Back has something to pop too.
 * - When the last popup closes some other way — ✕, Escape, an outside click —
 *   and the guard is still the current entry, it is popped with
 *   `history.back()` and that one `popstate` is ignored. So a click-closed
 *   popup leaves nothing behind for Back to step through.
 * - A guard left *under* a route change (a search result that navigates and
 *   closes the panel in one go) cannot be popped without undoing the
 *   navigation, so it stays — and is skipped the moment Back lands on it.
 *
 * - A router *replace* while a guard is current (a filter picked from a
 *   popup) lands on the guard, not on the entry under it. When the guard is
 *   popped, that URL is carried down onto the entry below and the router is
 *   told, so the pick survives the popup closing.
 *
 * Reconciling is deferred to one scheduled pass, so a popup that closes and
 * another that opens in the same tick (a menu item opening a dialog) keep the
 * guard rather than popping and re-pushing it.
 *
 * The logic is written against {@link HistoryPort} so it can be tested without
 * a DOM; {@link browserBackStack} wires it to `window.history`.
 *
 * The Android shell does not use guards at all. Its Back goes through the
 * webview's `goBack()`, which may skip entries Chromium decides were added
 * without a gesture, and with nothing left to go back to the app quits with a
 * popup still open. Tauri can hand us the button itself, so there
 * {@link createNativeBackStack} closes the popup without touching the history.
 */

import { invoke } from "@tauri-apps/api/core";
import { onBackButtonPress } from "@tauri-apps/api/app";

import { isTauri } from "@/platform/isTauri";

/** Marks a history entry as ours. Nothing else in the app writes it. */
export const GUARD_KEY = "__buseeBackGuard";

export type HistoryPort = {
  /** `history.state` of the current entry. */
  readonly state: unknown;
  /** Push a new entry with this state at the current URL. */
  push: (state: unknown) => void;
  back: () => void;
  /** Subscribe to `popstate`. Returns the unsubscribe. */
  onPop: (listener: () => void) => () => void;
  /** The current entry's URL. Needed only to carry a replace off a guard. */
  readonly href?: string;
  /** `replaceState` the current entry, bypassing the guard-keeping wrapper. */
  replace?: (state: unknown, href: string) => void;
  /** Tell the router the current URL changed (a synthetic `popstate`). */
  notify?: () => void;
};

export type BackStackOptions = {
  /** Runs `reconcile` later. Defaults to a macrotask. */
  schedule?: (task: () => void) => void;
  /**
   * Runs `task` once our own `history.back()` has had ample time to land.
   * There is one case where it never does — a guard that is the very first
   * entry, restored by a reload — and waiting for it forever would swallow the
   * user's next Back. Defaults to 500ms.
   */
  giveUp?: (task: () => void) => void;
};

export type BackStack = {
  /**
   * A popup just opened. `close` is what Back should do to it; the returned
   * function says it has closed (by any route) and must be called exactly once.
   */
  register: (close: () => void) => () => void;
  /**
   * The router just replaced the current guard's URL (a filter picked from a
   * popup, `?peek=` cleared). The entry under the guard still has the old URL,
   * so when the guard is popped — by Back or by the cleanup — that URL is
   * carried down onto it; otherwise the pick would be undone.
   */
  guardRewritten: () => void;
  /** How many popups are open, for tests and debugging. */
  readonly depth: number;
  /** Stop listening to the history. */
  dispose: () => void;
};

export function isGuard(state: unknown): boolean {
  return typeof state === "object" && state !== null && (state as Record<string, unknown>)[GUARD_KEY] === true;
}

/** The router's own state, kept, with the guard flag on top. */
export function guardState(state: unknown): Record<string, unknown> {
  const base = typeof state === "object" && state !== null ? (state as Record<string, unknown>) : {};
  return { ...base, [GUARD_KEY]: true };
}

export function createBackStack(history: HistoryPort, options: BackStackOptions = {}): BackStack {
  const schedule = options.schedule ?? ((task) => void setTimeout(task, 0));
  const giveUp = options.giveUp ?? ((task) => void setTimeout(task, 500));

  type Entry = { close: () => void };
  const entries: Entry[] = [];

  /** A `history.back()` of ours is in flight; its `popstate` is not the user. */
  let expectingPop = false;
  let popCount = 0;
  let scheduled = false;
  /** URL a rewritten guard had, and the router index it shares with its base. */
  let carry: { href: string; idx: unknown } | null = null;
  /** Our own synthetic `popstate` is being dispatched. */
  let announcing = false;

  function routerIndex(state: unknown): unknown {
    return typeof state === "object" && state !== null ? (state as Record<string, unknown>).idx : undefined;
  }

  /** Landed on the guard's own base entry: give it the URL the guard ended with. */
  function carryDown() {
    if (!carry || isGuard(history.state)) {
      return;
    }
    const pending = carry;
    carry = null;
    if (routerIndex(history.state) !== pending.idx || !history.replace || history.href === undefined) {
      return;
    }
    if (history.href === pending.href) {
      return;
    }
    history.replace(history.state, pending.href);
    if (history.notify) {
      announcing = true;
      try {
        history.notify();
      } finally {
        announcing = false;
      }
    }
  }

  function popOwnEntry() {
    expectingPop = true;
    const issued = popCount;
    history.back();
    giveUp(() => {
      if (expectingPop && popCount === issued) {
        expectingPop = false;
        // Only when something is open: with nothing open, reconcile would just
        // issue the same dead `back()` again.
        if (entries.length > 0) {
          requestReconcile();
        }
      }
    });
  }

  function reconcile() {
    scheduled = false;
    if (expectingPop) {
      // Settle once our own pop has landed; the pop handler reschedules.
      return;
    }
    const onGuard = isGuard(history.state);
    if (entries.length > 0 && !onGuard) {
      carry = null;
      history.push(guardState(history.state));
    } else if (entries.length === 0 && onGuard) {
      popOwnEntry();
    }
  }

  function requestReconcile() {
    if (scheduled) {
      return;
    }
    scheduled = true;
    schedule(reconcile);
  }

  const unsubscribe = history.onPop(() => {
    if (announcing) {
      return;
    }
    popCount += 1;
    carryDown();
    if (expectingPop) {
      expectingPop = false;
      // Something may have opened while our pop was in flight.
      requestReconcile();
      return;
    }
    const top = entries[entries.length - 1];
    if (top) {
      // Only ask. A popup may decline (a dirty form opens "Discard?" instead),
      // and reconcile then re-arms the guard because it is still registered.
      top.close();
      requestReconcile();
      return;
    }
    if (isGuard(history.state)) {
      // A stale guard under a route change, or one restored by a reload.
      popOwnEntry();
    }
  });

  // A reload restores `history.state`, so the app can start on a guard.
  requestReconcile();

  return {
    register(close) {
      const entry: Entry = { close };
      entries.push(entry);
      requestReconcile();
      let released = false;
      return () => {
        if (released) {
          return;
        }
        released = true;
        const index = entries.indexOf(entry);
        if (index !== -1) {
          entries.splice(index, 1);
        }
        requestReconcile();
      };
    },
    guardRewritten() {
      if (isGuard(history.state) && history.href !== undefined) {
        carry = { href: history.href, idx: routerIndex(history.state) };
      }
    },
    get depth() {
      return entries.length;
    },
    dispose: unsubscribe,
  };
}

/**
 * For a shell that hands over the Back button: `press` closes the topmost popup,
 * and `leave` runs only when nothing is open.
 */
export function createNativeBackStack(leave: () => void): BackStack & { press: () => void } {
  const entries: Array<{ close: () => void }> = [];
  return {
    press() {
      const top = entries[entries.length - 1];
      if (top) {
        top.close();
      } else {
        leave();
      }
    },
    register(close) {
      const entry = { close };
      entries.push(entry);
      return () => {
        const index = entries.indexOf(entry);
        if (index !== -1) {
          entries.splice(index, 1);
        }
      };
    },
    guardRewritten() {},
    get depth() {
      return entries.length;
    },
    dispose() {},
  };
}

/** The previous page, or out of the app from the first one. */
function leaveAndroidPage() {
  const state = window.history.state as { idx?: unknown } | null;
  if (typeof state?.idx === "number" && state.idx > 0) {
    window.history.back();
  } else {
    void invoke("exit_app");
  }
}

/* -------------------------------------------------------------------------- */

let shared: BackStack | null | undefined;

/**
 * The app's one stack, or `null` where Back is not a thing to design for.
 *
 * Only a touch-first device gets it — a phone, the Android shell, a tablet. On
 * the desktop every menu that opened would push an entry and every Escape pop
 * one, and each pop re-renders the router for a gesture (a mouse Back button)
 * nobody uses on a popup. The phone is where the problem was reported.
 *
 * Call it once at boot: on Android the button only comes to us after the
 * listener is in place.
 */
export function browserBackStack(): BackStack | null {
  if (shared !== undefined) {
    return shared;
  }
  if (typeof window === "undefined") {
    shared = null;
    return shared;
  }
  if (isTauri() && /Android/i.test(navigator.userAgent)) {
    const stack = createNativeBackStack(leaveAndroidPage);
    void onBackButtonPress(() => stack.press());
    shared = stack;
    return shared;
  }
  if (!window.matchMedia?.("(pointer: coarse)").matches) {
    shared = null;
    return shared;
  }

  const replaceRaw = window.history.replaceState.bind(window.history);
  const stack = createBackStack({
    get state() {
      return window.history.state;
    },
    get href() {
      return window.location.href;
    },
    replace: (state, href) => replaceRaw(state, "", href),
    notify: () => window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state })),
    push: (state) => window.history.pushState(state, ""),
    back: () => window.history.back(),
    onPop: (listener) => {
      window.addEventListener("popstate", listener);
      return () => window.removeEventListener("popstate", listener);
    },
  });
  keepGuardThroughReplace(window.history, () => stack.guardRewritten());
  shared = stack;
  return shared;
}

/**
 * A query write while a popup is open (`?peek=` being cleared, a filter) is a
 * router `replaceState` over the guard. Without this the guard flag would be
 * replaced away and the entry would turn into a plain page Back has to step
 * through. The router never reads the flag, so carrying it is invisible to it.
 *
 * `onCarry` runs after such a replace, so the stack can carry the new URL down
 * to the entry under the guard when the guard is popped.
 */
export function keepGuardThroughReplace(
  history: Pick<History, "state" | "replaceState">,
  onCarry?: () => void,
) {
  const replace = history.replaceState.bind(history);
  history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
    const carry = isGuard(history.state) && !isGuard(data);
    replace(carry ? guardState(data) : data, unused, url);
    if (carry) {
      onCarry?.();
    }
  };
}
